import React, { type SyntheticEvent, useCallback, useMemo, useRef } from 'react';

import { type UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { FlagsProvider } from '@atlaskit/flag';
import { fg } from '@atlaskit/platform-feature-flags';
import { type TriggerProps } from '@atlaskit/popup';
import { useAnalyticsEvents } from '@atlaskit/teams-app-internal-analytics';
import { ModalContextProvider } from '@atlassian/entry-point-modal-context/ModalContextProvider';
import { PopupTrigger } from '@atlassian/entry-point-popup';
import { usePeopleTeamAnalyticsEvents } from '@atlassian/people-teams-ui/analytics';

import PopupComponent from '../../../../common/no-background-popup-component';
import { getSiteId } from '../../../../common/utils/ari';
import { isAnalyticsEventsMigrated } from '../../../../common/utils/statsig';
import { isTeamBiDirectionalEnabled } from '../../../../common/utils/utils';
import { TeamSelectorProvider } from '../../../../controllers/team-selector-context';
import { asyncTeamSelectorEntryPoint } from '../../../team-selector/entrypoint';
import { useTeamBiDirectionalExp } from '../../../team-selector/hooks/use-team-connection-exp';
import TeamSelectorSkeleton from '../../../team-selector/team-selector-skeleton';
import { getContainerType } from '../../../team-selector/utils/getContainerType';
import { TeamButtonTrigger } from '../team-button-trigger';

type TeamButtonComponentProps = {
	containerId: string;
	orgId?: string;
	Wrapper?: React.ElementType;
	numberOfTeams?: number;
};

export const TeamButtonComponentContent = ({
	numberOfTeams,
	containerId,
	Wrapper = React.Fragment,
}: TeamButtonComponentProps): React.JSX.Element | null => {
	const { fireEvent: DEPRECATED_fireEvent } = usePeopleTeamAnalyticsEvents();
	const { fireEvent } = useAnalyticsEvents();
	const triggerRef = useRef<HTMLButtonElement | null>(null);

	const { isTeamBiDirectionalEnabled: isExperimentEnabled } = useTeamBiDirectionalExp();

	const handleButtonClick = useCallback(
		(event: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => {
			const element = event.currentTarget as HTMLElement;
			const isExpanded = isExperimentEnabled
				? element.getAttribute('aria-expanded') === 'true'
				: true;
			if (isExpanded) {
				const result = analyticsEvent?.context.find(
					(item) => item.attributes && item.attributes.scope !== undefined,
				);
				if (isAnalyticsEventsMigrated()) {
					fireEvent('ui.teamButton.clicked', {
						teamsCount: numberOfTeams ?? 0,
						scope: result?.attributes?.scope ?? '',
						version: isExperimentEnabled ? 'm2' : 'm1',
					});
				} else {
					DEPRECATED_fireEvent('ui.teamButton.clicked', {
						teamsCount: numberOfTeams ?? 0,
						scope: result?.attributes?.scope ?? '',
						version: isExperimentEnabled ? 'm2' : 'm1',
					});
				}
			}
		},
		[fireEvent, DEPRECATED_fireEvent, numberOfTeams, isExperimentEnabled],
	);

	const getTriggerProps = useCallback(
		(triggerProps: TriggerProps) => {
			if (!isExperimentEnabled) {
				return triggerProps;
			}
			const setRef = (node: HTMLButtonElement | null) => {
				triggerRef.current = node;
				if (typeof triggerProps.ref === 'function') {
					triggerProps.ref(node);
				} else if (triggerProps.ref) {
					(triggerProps.ref as React.MutableRefObject<HTMLButtonElement | null>).current = node;
				}
			};
			return { ...triggerProps, ref: setRef };
		},
		[isExperimentEnabled],
	);

	const onFocusTrigger = useCallback(() => {
		setTimeout(() => {
			if (triggerRef.current) {
				triggerRef.current.focus();
			}
		}, 0);
	}, [triggerRef]);

	const entryPointProps = {
		// Remove as part of M1 FG cleanup
		...(isExperimentEnabled ? {} : { containerId }),
		onModalCloseComplete: onFocusTrigger,
	};

	const entryPointPropsMemoized = useMemo(
		() => ({
			// Remove as part of M1 FG cleanup
			...(isExperimentEnabled ? {} : { containerId }),
			onModalCloseComplete: onFocusTrigger,
		}),
		[isExperimentEnabled, containerId, onFocusTrigger],
	);

	const trigger = (triggerProps: TriggerProps) => (
		<Wrapper>
			<TeamButtonTrigger
				triggerProps={getTriggerProps(triggerProps)}
				// numberOfTeams will never be undefined/0 as we check for the below.
				numberOfTeams={numberOfTeams ?? 0}
				onClick={handleButtonClick}
			/>
		</Wrapper>
	);

	const triggerMemoized = useCallback(
		(triggerProps: TriggerProps) => (
			<Wrapper>
				<TeamButtonTrigger
					triggerProps={getTriggerProps(triggerProps)}
					// numberOfTeams will never be undefined/0 as we check for the below.
					numberOfTeams={numberOfTeams ?? 0}
					onClick={handleButtonClick}
				/>
			</Wrapper>
		),
		[Wrapper, getTriggerProps, numberOfTeams, handleButtonClick],
	);

	if (numberOfTeams === undefined) {
		return null;
	}

	if (numberOfTeams === 0 && !isExperimentEnabled) {
		return null;
	}

	return (
		<FlagsProvider>
			<ModalContextProvider>
				<PopupTrigger
					entryPoint={asyncTeamSelectorEntryPoint}
					entryPointProps={
						fg('jfp-magma-stability-platform-3') ? entryPointPropsMemoized : entryPointProps
					}
					entryPointParams={{ containerId }}
					trigger={fg('jfp-magma-stability-platform-3') ? triggerMemoized : trigger}
					fallback={<TeamSelectorSkeleton />}
					placement="bottom-start"
					strategy="absolute"
					popupComponent={PopupComponent}
				/>
			</ModalContextProvider>
		</FlagsProvider>
	);
};

export const TeamButtonComponent = (props: TeamButtonComponentProps): React.JSX.Element => {
	const { containerId, orgId } = props;
	const containerType = getContainerType(containerId);
	const isExperimentEnabled = isTeamBiDirectionalEnabled(containerType);

	if (isExperimentEnabled) {
		return (
			<TeamSelectorProvider
				containerId={containerId}
				containerType={containerType}
				// cloudId is guaranteed to be a string as we will always receive a valid containerId
				cloudId={getSiteId(containerId) || ''}
				// orgId will always be available if isTeamBiDirectionalEnabled is true
				orgId={orgId || ''}
			>
				<TeamButtonComponentContent {...props} />
			</TeamSelectorProvider>
		);
	}

	return <TeamButtonComponentContent {...props} />;
};
